import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateUtils } from 'src/app/_commons/date-utils';
import { GPlanInput } from 'src/app/_models/gplan-input';
import { TopologyConfig, TopologyList, TopologyWithRoot } from 'src/app/_models/topology-list';
import { TopologyPlanningOverview } from 'src/app/_models/topology-overview';
import { ApplicationsResultsService } from 'src/app/_services/applications-results-service/applications-results.service';
import { CommunicationService } from 'src/app/_services/communication-service/communication.service';
import { GPlanService } from 'src/app/_services/gplan-service/gplan.service';

@Component({
  selector: 'app-planning-overview',
  templateUrl: './planning-overview.component.html',
  styleUrls: ['./planning-overview.component.css']
})
export class PlanningOverviewComponent implements OnInit {

  DateUtils = DateUtils;

  public isSidebarOpen: boolean;
  public planningResults: Array<TopologyPlanningOverview> = new Array<TopologyPlanningOverview>();
  public topologies: TopologyList;

  planningResultsColumn: any[] = [];
  isSearching: boolean[] = Array(12).fill(false);
  searchText: string[] = Array(12).fill('');

  @ViewChild('searchInput', { static: false }) searchInputRef!: ElementRef;
  private shouldFocusInput = false;

  public jumpButtonPressed: boolean = false;

  constructor(
    private applicationsResultsService: ApplicationsResultsService,
    private communicationService: CommunicationService,
    public translate: TranslateService,
    private router: Router,
    private gplanService: GPlanService
  ) { 
    const lang = localStorage.getItem('Language');
    this.translate.use(lang);
  }

  ngOnInit() {

    this.applicationsResultsService.setSidebarOpen(true);
    this.isSidebarOpen = this.applicationsResultsService.getSidebarOpen();

    if(!localStorage.getItem('planningResults')){
      this.getPlanningResults();
    }else{
      this.parseCachedPlanningResults(JSON.parse(localStorage.getItem('planningResults')));
    }
    this.topologies = JSON.parse(localStorage.getItem('topologies'));
  }

  resetDefaultTopology(){
    this.communicationService.setTopologyId(null);
  }

  getPlanningResults(){
    this.applicationsResultsService.getPlanningResults().subscribe((data: object) => {
      // console.log('Planning Results: ', data);

      Object.entries(data).forEach(([key, value]) => {
        let displayName = (this.topologies.topologiesWithRoots.find((topology: TopologyWithRoot) => topology.TopologyId === key).displayName 
          || (this.topologies.topologiesWithRoots.find((topology: TopologyWithRoot) => topology.TopologyId === key).internalDsoId)
          + " - " + (this.topologies.topologiesWithRoots.find((topology: TopologyWithRoot) => topology.TopologyId === key).TopologyId)) || "N/A";
        
        this.getListOfScenariosForSelectedTopology((this.topologies.topologiesWithRoots.find((topology: TopologyWithRoot) => topology.TopologyId === key)).TopologyId).subscribe((scenariosMap: Map<number, string>) => {
          value.forEach((item: any) => {
            this.planningResults.push({
              topologyId: displayName,
              maxNodeLoad: item.maxNodeLoad || "N/A",
              maxVoltage: item.maxVoltage || "N/A",
              totalLaCableViolationCount: item.totalLaCableViolationCount || "N/A",
              totalLaNodeViolationCount: item.totalLaNodeViolationCount || "N/A",
              totalLowerBoundVoltageViolationCount: item.totalLowerBoundVoltageViolationCount || "N/A",
              totalUpperBoundVoltageViolationCount: item.totalUpperBoundVoltageViolationCount || "N/A",
              minVoltage: item.minVoltage || "N/A",
              maxCableLoad: item.maxCableLoad || "N/A",
              scenarioId: item.scenarioId ? item.scenarioId + " - " + scenariosMap.get(item.scenarioId) || "N/A" : "N/A",
              intervalStartTimestamp: item.intervalStartTimestamp || "N/A",
              intervalEndTimestamp: item.intervalEndTimestamp || "N/A"
            });
          });

          // console.log('Parsed Planning Results:', this.planningResults);
          localStorage.setItem('planningResults', JSON.stringify(this.planningResults));
        });
          
      });
    });
  }

  /**
   * This function retrieves the list of scenarios for a given topology ID
   * It returns an Observable that emits a Map of scenario IDs and their names
   */
  getListOfScenariosForSelectedTopology(topologyId: string): Observable<Map<number, string>> {
    return this.gplanService.getListAvailableScenariosForOverallResults(topologyId).pipe(
      map((data: Array<GPlanInput>) => {
        const scenariosMap = new Map<number, string>();
        data.forEach((scenario: GPlanInput) => {
          scenariosMap.set(scenario.id, scenario.name);
        });
        return scenariosMap;
      })
    );
  }

  parseCachedPlanningResults(planningResults: any){
    planningResults.forEach((item: any) => {
      // item.forEach((value: any) => {
        this.planningResults.push({
          topologyId: item.topologyId || "N/A",
          maxNodeLoad: item.maxNodeLoad || "N/A",
          maxVoltage: item.maxVoltage || "N/A",
          minVoltage: item.minVoltage || "N/A",
          totalLaCableViolationCount: item.totalLaCableViolationCount || "N/A",
          totalLaNodeViolationCount: item.totalLaNodeViolationCount || "N/A",
          totalLowerBoundVoltageViolationCount: item.totalLowerBoundVoltageViolationCount || "N/A",
          totalUpperBoundVoltageViolationCount: item.totalUpperBoundVoltageViolationCount || "N/A",
          maxCableLoad: item.maxCableLoad || "N/A",
          scenarioId: item.scenarioId || "N/A",
          intervalStartTimestamp: item.intervalStartTimestamp || "N/A",
          intervalEndTimestamp: item.intervalEndTimestamp || "N/A"
        });
      // });
    });
    // console.log('Parsed Cached Planning Results:', this.planningResults);
  }

  sortTable(columnIndex: number): void {
    const table = document.getElementById("sortableTable");
    if (!table) return;
  
    const rows = Array.from((table as HTMLTableElement).rows).slice(1); // Exclude header row
    let isAscending = table.getAttribute("data-sort-order") === "asc";
  
    const isNumeric = rows.some(row => {
      const value = row.cells[columnIndex].innerText.trim();
      return !isNaN(parseFloat(value)) && value !== '' && value !== 'N/A' && value !== 'n/a';
    });

    const isDate = rows.some(row => {
      const value = row.cells[columnIndex].innerText.trim();
      return !isNaN(Date.parse(value)) && value !== '' && value !== 'N/A' && value !== 'n/a';
    })
  
    rows.sort((rowA, rowB) => {
      const cellA = rowA.cells[columnIndex].innerText.trim();
      const cellB = rowB.cells[columnIndex].innerText.trim();

      if (isNumeric) {
        const numbA = (cellA === 'N/A' || cellA === '' || isNaN(parseFloat(cellA))) ? -Infinity : parseFloat(cellA);
        const numbB = (cellB === 'N/A' || cellB === '' || isNaN(parseFloat(cellB))) ? -Infinity : parseFloat(cellB);

        return isAscending ? numbA - numbB : numbB - numbA;
      } 
      
      if(isDate){
        const parseDate = (dateString: string) => {
          const date = new Date(dateString);
          return !isNaN(date.getTime()) ? date : Infinity
        };

        const dateA = parseDate(cellA);
        const dateB = parseDate(cellB);
        return isAscending 
          ? (dateA instanceof Date ? dateA.getTime() : 0) - (dateB instanceof Date ? dateB.getTime() : 0) 
          : (dateB instanceof Date ? dateB.getTime() : 0) - (dateA instanceof Date ? dateA.getTime() : 0);
      }
      return isAscending ? cellA.localeCompare(cellB) : cellB.localeCompare(cellA);
    });
  
    table.setAttribute("data-sort-order", isAscending ? "desc" : "asc");
    rows.forEach(row => table.appendChild(row));
  }

  toggleSearch(index: number): void {
    setTimeout(() => {
      if(this.jumpButtonPressed){
        this.jumpButtonPressed = false;
        return;
      }

      this.isSearching = this.isSearching.map((_, i) => i === index ? !this.isSearching[i] : false);

      if(this.isSearching.every(value => value === false)){
        this.searchText = Array(12).fill('');
      }
      // console.log("Search Toggled:", this.isSearching);

      if(this.isSearching[index]){
        this.shouldFocusInput = true;
      }
  
    }, 0);
    
  }

  ngAfterViewChecked(){
    if(this.shouldFocusInput && this.searchInputRef){
      this.searchInputRef.nativeElement.focus();
      // this.shouldFocusInput = false;
      // console.log('Focused on search input' + this.shouldFocusInput);
    }
  }

  jumpTopologyMouseDown(){
    this.jumpButtonPressed = true;
  }

  filteredResults(): any[] {
    
    if(this.isSearching.every(value => value === false)){
      this.searchText = Array(12).fill('');
    }
    if(this.searchText.every(text => !text)){
      return this.planningResults;
    }
    return this.planningResults.filter(item => {
      return this.searchText.every((text, index) => {
        if(!text) return true;
        const value = Object.values(item)[index];
        return value.toString().toLowerCase().includes(text.toLowerCase())
      })
    })
  }

  getDispalyNamesOfTopologies() {
    const listOfTopologies: TopologyList =  JSON.parse(localStorage.getItem('topologies'));
  }

  jumpToTheTopology(topologyId: string){
    this.topologies.topologiesWithRoots.forEach((topology: TopologyWithRoot) => {
      if(topology.displayName === topologyId || topology.internalDsoId + " - " + topology.TopologyId === topologyId){
        // console.log('Jump to Topology:', topology.TopologyId);
        let topologyConfig: TopologyConfig = new TopologyConfig(
          topology.TopologyId,
          topology.latitude,
          topology.longitude,
          topology.lastTopologyChange
        );

        this.router.navigate(['home']);
        this.communicationService.setTopologyId(topologyConfig);
      }
    })

    // console.log('Jump to Topology:', topologyId);
  }

  getViolationColoring(topologyOverview: any){
    if( topologyOverview.totalLaCableViolationCount > 0 || topologyOverview.totalLaNodeViolationCount > 0 || topologyOverview.totalLowerBoundVoltageViolationCount > 0 || topologyOverview.totalUpperBoundVoltageViolationCount > 0){
      return 'red';
    }
  }
}
